import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { type GridRowsProp , type GridColDef } from '@mui/x-data-grid';
import { type FC, useMemo } from 'react';

import CodeMetadataDelete from './CodeMetadataDelete.js';
import CodeMetadataForm from './CodeMetadataForm.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Code, type CodeMetadata } from '../../models/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

interface CodeMetadataTableProps {
  selectedCode: Code;
  refetch: () => void;
}

const CodeMetadataTable: FC<CodeMetadataTableProps> = ({ selectedCode, refetch }) => {
  const {open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen} = useDialogControls<CodeMetadata>(selectedCode.metadata);
  const {open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete} = useDialogControls<CodeMetadata>(selectedCode.metadata);

  const { canCreateCode, canUpdateCode, canDeleteCode } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'prompt', headerName: 'Prompt', flex: 1 },
      {
        field: 'inputType',
        headerName: 'Type',
        flex: 1,
      },
    ];
    if (canUpdateCode || canDeleteCode) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canUpdateCode,
          canDelete: canDeleteCode,
        }),
      });
    }
    return cols;
  }, [canUpdateCode, canDeleteCode, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(
    () =>
      selectedCode?.metadata?.map((item) => ({
        id: item.id,
        prompt: item.prompt,
        inputType: item.inputType,
      })) || [],
    [selectedCode],
  );

  return (
    <Box sx={{ width: '40%', minWidth: 0 }}>
      <Stack direction={'row'} justifyContent={'space-between'} sx={{ my: 1 }}>
        <h2 style={{ margin: 0 }}>Metadata</h2>
        {canCreateCode ? (
          <Tooltip arrow color="primary" placement="bottom" title={'Add a new metadata'}>
            <IconButton color="inherit" aria-label="Add a new metadata" onClick={() => setFormOpen(true)} >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Table
        columns={columns}
        rows={rows}
        NoRowsOverlayText={'No metadata available.'}
      />
      <Typography variant={'caption'}>
        Metadata is additional information you can save for a code (e.g. notes).
      </Typography>
      <CodeMetadataForm
        open={formOpen}
        onClose={resetForm}
        refetch={refetch}
        codeMetadata={formData}
        codeId={selectedCode.id}
      />
      <CodeMetadataDelete
        open={deleteOpen}
        onClose={resetDelete}
        codeMetadata={deleteData}
        refetch={refetch}
      />
    </Box>
  );
};

export default CodeMetadataTable;
